import React from "react";
import { Box, SimpleGrid, Text, useColorMode } from "@chakra-ui/react";
import { Chart } from "react-google-charts";
import InsightsSection from "./InsightsSection";
import PlaceholderTile from "./PlaceholderTile";

const RiskRewardSection = ({ riskReward = {}, currencySymbol = "$" }) => {
  const { colorMode } = useColorMode();
  const cardBg = colorMode === "light" ? "gray.50" : "gray.600";
  const labelColor = colorMode === "light" ? "gray.600" : "gray.300";

  const hasData = riskReward && Object.keys(riskReward).length > 0;
  const distribution = Array.isArray(riskReward.rr_distribution)
    ? riskReward.rr_distribution
    : [];

  const chartData = distribution.length
    ? [["Bucket", "Trades", { role: "style" }],
       ...distribution.map((b) => [b.bucket, b.count, "#3182CE"])]
    : null;

  const chartOptions = {
    chartArea: { width: "70%", height: "75%" },
    backgroundColor: colorMode === "light" ? "#FDFDFD" : "#1A202C",
    legend: "none",
    hAxis: {
      title: "Trades",
      titleTextStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
      textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
    },
    vAxis: { textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" } },
  };

  return (
    <InsightsSection
      title="Risk/Reward & Cost Basis"
      description="How much you risk vs. reward, and how well your capital deployment performs."
    >
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Avg R:R</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasData && riskReward.avg_rr != null ? riskReward.avg_rr.toFixed(2) : "—"}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Avg % Win</Text>
          <Text fontSize="lg" fontWeight="bold" color="green.400">
            {hasData && riskReward.avg_percent_win != null
              ? `${riskReward.avg_percent_win.toFixed(2)}%`
              : "—"}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Avg % Loss</Text>
          <Text fontSize="lg" fontWeight="bold" color="red.400">
            {hasData && riskReward.avg_percent_loss != null
              ? `${riskReward.avg_percent_loss.toFixed(2)}%`
              : "—"}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Return on Cost</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasData && riskReward.return_on_cost != null
              ? `${riskReward.return_on_cost.toFixed(2)}%`
              : "—"}
          </Text>
          <Text fontSize="xs" color={labelColor}>
            {hasData && riskReward.total_cost_deployed != null
              ? `${currencySymbol}${Number(riskReward.total_cost_deployed).toFixed(0)} deployed`
              : ""}
          </Text>
        </Box>
      </SimpleGrid>

      {distribution.length ? (
        <Box bg={colorMode === "light" ? "white" : "gray.700"} rounded="md" h="280px">
          <Chart
            chartType="BarChart"
            width="100%"
            height="100%"
            data={chartData}
            options={chartOptions}
          />
        </Box>
      ) : (
        <PlaceholderTile message="No R:R data yet — set rr on a trade to see a distribution." />
      )}
    </InsightsSection>
  );
};

export default RiskRewardSection;
